import type { TextEngine } from '@meitu/x-design-text';
import { ceilNonNegative, isNonNegativeFiniteNumber, isPositiveFiniteNumber } from '@/utils/math-kit';
import { LIST_MARKER_DICT, type ListStyle } from '@/version-types';
import { MARKER_GAP_EM } from '@/version-types/constants';
import { getCharTypedMarkerText } from '../list';

/**
 * 水平文本渲染链路使用的字符型列表样式。
 *
 * 第一阶段只覆盖水平文本，`custom` 资源型 marker 会使用独立的同步尺寸配置测量。
 */
type CharTypedListStyle = Exclude<ListStyle, 'custom'>;

/**
 * 字符型列表 marker 的测量输入。
 *
 * @param type - 'char'，表示该列表 marker 由字体字符绘制。
 * @param style - CharTypedListStyle，当前列表样式。
 * @param order - number，1-based 列表序号；需要测最宽 marker 时传入当前段落总数。
 * @param fontFamily - string，已注册到文本引擎的字体族名称。
 * @param fontSize - number，可选；marker 测量使用的字号，不传时使用文本模型字号。
 */
export type MeasureCharMarkerInput = {
    type: 'char';
    style: CharTypedListStyle;
    order: number;
    fontFamily: string;
    fontSize?: number;
};

/**
 * 资源型列表 marker 的同步尺寸测量输入。
 *
 * image 与 SVG marker 的实际资源加载由后续 asset loader 承担；这里仅消费已经同步可得的
 * 素材基准宽度、字号和缩放倍率，保证测量入口不拥有异步资源生命周期。
 *
 * @param type - 'custom'，表示该列表 marker 来自 image 或 SVG 素材。
 * @param baseContentTextSize - number，素材设计时对应的正文基准字号。
 * @param iconWidth - number，素材基准宽度。
 * @param scale - number，可选；用户缩放倍率，不传时使用 1。
 * @param fontSize - number，可选；marker 测量使用的字号，不传时使用文本模型字号。
 */
export type MeasureCustomMarkerInput = {
    type: 'custom';
    baseContentTextSize: number;
    iconWidth: number;
    scale?: number;
    fontSize?: number;
};

/**
 * 水平文本模型中的列表缩进测量配置。
 *
 * @param type - marker 测量类型，区分字符型和资源型。
 * @returns 该类型只描述输入结构，具体缩进值由 marker helper 计算。
 */
export type MeasureListMarkerInput = MeasureCharMarkerInput | MeasureCustomMarkerInput;

/**
 * 使用文本引擎测量字符型列表 marker 缩进。
 *
 * @param engine - TextEngine，已经注册字体的文本引擎实例。
 * @param options - MeasureCharMarkerInput，字符型 marker 测量配置。
 * @param fallbackFontSize - number，配置未覆盖字号时使用的正文字号。
 * @returns number，marker 宽度加正文间距；失败时返回 0。
 */
const measureCharTypedMarker = (
    engine: TextEngine,
    options: MeasureCharMarkerInput,
    fallbackFontSize: number,
): number => {
    const fontSize = isPositiveFiniteNumber(options.fontSize) ? options.fontSize : fallbackFontSize;

    if (
        options.order <= 0 ||
        !options.fontFamily ||
        !isPositiveFiniteNumber(fontSize) ||
        engine.getRegisteredFontCount() === 0
    ) {
        return 0;
    }

    const text =
        options.style === 'lower-latin'
            ? Array.from({ length: Math.min(options.order, LIST_MARKER_DICT['lower-latin'].length) }, (_, index) =>
                  getCharTypedMarkerText(options.style, index + 1),
              ).join('\n')
            : getCharTypedMarkerText(options.style, options.order);
    if (!text) return 0;

    const buffer = engine.createBuffer({ fontSize });

    try {
        buffer.setText(text, { family: options.fontFamily });
        buffer.layout();

        return ceilNonNegative(buffer.getContentWidth()) + fontSize * MARKER_GAP_EM;
    } catch {
        return 0;
    } finally {
        buffer.dispose();
    }
};

/**
 * 根据同步素材尺寸计算 image/SVG 类型列表 marker 缩进。
 *
 * @param options - MeasureCustomMarkerInput，资源型 marker 的同步尺寸配置。
 * @param fallbackFontSize - number，配置未覆盖字号时使用的正文字号。
 * @returns number，缩放后的素材宽度加正文间距；输入非法时返回 0。
 */
export function measureCustomMarker(options: MeasureCustomMarkerInput, fallbackFontSize: number): number {
    const fontSize = isPositiveFiniteNumber(options.fontSize) ? options.fontSize : fallbackFontSize;

    if (!isPositiveFiniteNumber(options.baseContentTextSize) || !isNonNegativeFiniteNumber(options.iconWidth)) return 0;
    if (!isPositiveFiniteNumber(fontSize)) return 0;

    const scale =
        (fontSize / options.baseContentTextSize) * (isPositiveFiniteNumber(options.scale) ? options.scale : 1);

    return options.iconWidth * scale + fontSize * MARKER_GAP_EM;
}

/**
 * 根据共享水平文本模型中的列表配置计算 marker 缩进。
 *
 * @param engine - TextEngine，当前文本引擎实例。
 * @param options - MeasureListMarkerInput，可选；列表 marker 测量配置。
 * @param fallbackFontSize - number，列表配置未覆盖字号时使用的正文字号。
 * @returns number，marker 缩进；非列表或失败时返回 0。
 */
export const measureListMarker = (
    engine: TextEngine,
    options: MeasureListMarkerInput | null | undefined,
    fallbackFontSize: number,
): number => {
    if (!options) return 0;

    if (options.type === 'char') {
        return measureCharTypedMarker(engine, options, fallbackFontSize);
    }

    return measureCustomMarker(options, fallbackFontSize);
};
